/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 起票フォームの添付ステージング + 起票後アップロード。ファイルを client 検証してステージし、起票が成功して opsId が付いたら（submitted）採番済み id へ順次アップロードする。1 件失敗しても依頼は起票済みで詳細ページから追加し直せるため続行するが、失敗したファイルと理由（API のメッセージ）は確認画面に出して黙って落とさない。起票ページの行数肥大を避けるため添付関連の state・UI・アップロード進捗をこのコンポーネントに閉じ、常時マウントして起票→確認をまたいでも state を保持する
 * @graph-connects OpsSubmitPage [references] 起票ページ本体から添付ステージング UI とアップロードを分離
 * @graph-connects useUploadOpsAttachment [calls] ステージ済みファイルの起票後アップロードを実行
 * @graph-connects uploadFilesSequentially [calls] ステージ済みファイルを順に送り失敗を集める
 * @graph-connects OpsFailedUploads [references] 失敗したファイルと理由の表示
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import {
  formatFileSize,
  OPS_ATTACHMENT_MAX_BYTES,
  OPS_ATTACHMENT_MAX_COUNT,
} from '@cortex/ops-core';
import { useUploadOpsAttachment } from '../../../hooks/useOps.js';
import { OpsAttachmentInput, type OpsAttachmentRejectReason } from './OpsAttachmentInput.js';
import {
  OpsFailedUploads,
  uploadFilesSequentially,
  type FailedUpload,
} from './OpsFailedUploads.js';

/** OpsSubmitAttachments の props。 */
export interface OpsSubmitAttachmentsProps {
  /** 翻訳関数。 */
  t: TFunction;
  /** 起票が確定したか（true になり opsId が付いたらステージ済みをアップロードする）。 */
  submitted: boolean;
  /** 採番済み ops id（未起票は null）。 */
  opsId: string | null;
}

/**
 * 添付ステージング UI + 起票後アップロード。state（ステージ済みファイル・エラー・進捗）を内部に閉じ、常時マウントで
 * 起票→確認をまたいで保持する。submitted && opsId になったら 1 度だけ順次アップロードし、完了でステージをクリアする。
 * @graph-connects none
 */
export function OpsSubmitAttachments({
  t,
  submitted,
  opsId,
}: OpsSubmitAttachmentsProps): ReactNode {
  const uploadAttachment = useUploadOpsAttachment();
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failedUploads, setFailedUploads] = useState<FailedUpload[]>([]);
  // 同じ依頼へ二重アップロードしないためのガード（アップロード済みの opsId を覚える）。
  const uploadedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!submitted || opsId === null || uploadedForRef.current === opsId) return;
    uploadedForRef.current = opsId;
    // 「別の依頼を作成」でこのコンポーネントは残るので、前の依頼の失敗を次の依頼に持ち越さない。
    setFailedUploads([]);
    const files = stagedFiles;
    if (files.length === 0) return;
    void (async () => {
      setProgress({ done: 0, total: files.length });
      const failed = await uploadFilesSequentially(
        files,
        (file) => uploadAttachment.mutateAsync({ id: opsId, file }),
        (done) => {
          setProgress({ done, total: files.length });
        },
      );
      setProgress(null);
      setFailedUploads(failed);
      setStagedFiles([]);
    })();
  }, [submitted, opsId, stagedFiles, uploadAttachment]);

  const stageFiles = (files: File[]): void => {
    setAttachError(null);
    setStagedFiles((prev) => [...prev, ...files]);
  };

  const rejectFiles = (reason: OpsAttachmentRejectReason): void => {
    setAttachError(`ops.attachments.errors.${reason}`);
  };

  const removeStagedFile = (index: number): void => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-1.5">
      {!submitted && (
        <>
          <span className="text-sm font-medium text-[var(--color-text-primary)]">
            {t('ops.attachments.addLabel')}
          </span>
          <OpsAttachmentInput
            currentCount={stagedFiles.length}
            onAccept={stageFiles}
            onReject={rejectFiles}
            t={t}
          />
          {attachError !== null && (
            <p className="text-xs text-[var(--color-error)]">
              {t(attachError, {
                count: OPS_ATTACHMENT_MAX_COUNT,
                size: formatFileSize(OPS_ATTACHMENT_MAX_BYTES),
              })}
            </p>
          )}
          {stagedFiles.length > 0 && (
            <ul className="space-y-1">
              {stagedFiles.map((file, index) => (
                <li
                  key={`${file.name}-${String(index)}`}
                  className="flex items-center gap-2 text-xs"
                >
                  <span className="min-w-0 flex-1 truncate text-[var(--color-text-secondary)]">
                    {file.name}
                  </span>
                  <span className="shrink-0 text-[var(--color-text-muted)]">
                    {formatFileSize(file.size)}
                  </span>
                  <button
                    type="button"
                    aria-label={t('ops.attachments.removeStaged', { name: file.name })}
                    onClick={() => {
                      removeStagedFile(index);
                    }}
                    className="shrink-0 rounded px-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-error)]"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {opsId !== null && (
        <OpsFailedUploads
          failures={failedUploads}
          title={t('ops.attachments.errors.submitUploadFailed', { count: failedUploads.length })}
          t={t}
        >
          <Link
            to="/ops/requests/$id"
            params={{ id: opsId }}
            className="font-semibold text-[var(--color-accent-primary)] hover:underline"
          >
            {t('ops.attachments.errors.retryFromDetail')}
          </Link>
        </OpsFailedUploads>
      )}
      {progress !== null && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('ops.attachments.uploadProgress', { done: progress.done, total: progress.total })}
        </p>
      )}
    </div>
  );
}
